import { useApi } from '@src/app/context/Api';
import { useRoutesContext } from '@src/app/context/Routes/Routes';
import { NotFound } from '@src/entities/Criteria/ui/components/NotFound';
import { CreateButton } from '@src/features/CommonCriteria/Create';
import { EditForm } from '@src/features/CommonCriteria/Edit';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Loader } from '@src/shared/ui/components/Loader';
import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import { ToggleSwitch } from '@uhg-abyss/web/ui/ToggleSwitch';
import { motion } from 'framer-motion';
import { isEmpty, isNil, isNull } from 'lodash';
import React, { createRef, useEffect, useMemo, useRef, useState } from 'react';

import { Styles } from './includes/styles';

/**
 * Page: CommonCriteria
 *
 * Provides the user with a screen listing the common criteria to be used for filtering risk records.
 *
 * @returns {Element}
 * @constructor
 */
export function CommonCriteria() {
  const [showAll, setShowAll] = useState(false);

  const { currentRoute } = useRoutesContext();

  const { useApiQuery } = useApi('Criteria');
  const [ListCommonCriteria, { data, isFetching, isLoading, refetch }] = useApiQuery('ListCommonCriteria');

  /**
   * Fetch the common criteria data if it is not already loaded.
   */
  useEffect(() => {
    const payload = {
      page: 0,
      size: 9999,
      sort: 'name,asc',
    };

    if (showAll === false) {
      payload.isActive = true;
    }

    ListCommonCriteria(payload);
  }, [showAll]);

  const [focusedEntity, setFocusedEntity] = useState(null);

  const triggerRefs = useRef({});

  triggerRefs.current = useMemo(() => {
    return data?.content?.map((item, index) => {
      const currentRef = triggerRefs?.current?.[index];

      if (!isNil(currentRef)) {
        return currentRef;
      }

      return createRef();
    });
  }, [data, triggerRefs]);

  return (
    <ErrorHandler location="src/pages/private/Admin/CommonCriteria/CommonCriteria.jsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <Styles>
          <Grid>
            <Grid.Col
              span={{
                xs: '100%',
              }}
            >
              <Flex alignItems="center" direction="row">
                <Heading offset={0}>Common Criteria</Heading>
                <CreateButton focusedEntity={focusedEntity} refetch={refetch} setFocusedEntity={setFocusedEntity}>
                  Create
                </CreateButton>
              </Flex>
            </Grid.Col>

            <Grid.Col
              span={{
                xs: '100%',
              }}
            >
              <ToggleSwitch
                isChecked={showAll}
                label="Show all Criteria"
                onChange={(event) => {
                  return setShowAll(event?.target?.checked);
                }}
              />
            </Grid.Col>

            <Grid.Col
              span={{
                xs: '100%',
              }}
            >
              {isLoading || isFetching ? (
                <Loader verticalAlignment="start" />
              ) : (
                <React.Fragment>
                  {isEmpty(data?.content) ? (
                    <React.Fragment>
                      {showAll === true && (
                        <NotFound
                          button={<CreateButton refetch={refetch}>Create Criteria</CreateButton>}
                          icon="settings"
                          message="Get started by creating common criteria filters."
                          title="No Criteria Found"
                        />
                      )}
                      {showAll === false && (
                        <NotFound
                          button={
                            <Link
                              after={<IconSymbol css={{ fontSize: 'inherit !important' }} icon="double_arrow" />}
                              href={currentRoute?.path}
                              onClick={(event) => {
                                event.preventDefault();
                                setShowAll(true);
                              }}
                              size="$sm"
                            >
                              Show all Criteria
                            </Link>
                          }
                          icon="settings"
                          message="There is currently no activated criteria filters."
                          title="No Activated Criteria"
                        />
                      )}
                    </React.Fragment>
                  ) : (
                    <Accordion defaultValue={focusedEntity} isCollapsible>
                      {data?.content?.map((item, index) => {
                        return (
                          <Accordion.Item key={`${item?.name}`} value={item?.name}>
                            <Accordion.Trigger
                              onClick={() => {
                                if (!isNull(focusedEntity)) {
                                  if (item?.name === focusedEntity) {
                                    setFocusedEntity(item?.name);
                                  } else {
                                    setFocusedEntity(null);
                                  }
                                } else {
                                  setFocusedEntity(item?.name);
                                }
                              }}
                              ref={triggerRefs?.current?.[index]}
                            >
                              <Accordion.Header>
                                <Heading color="var(--abyss-colors-gray7)" offset={3}>
                                  <Layout.Group space={12}>
                                    {item?.name}

                                    {item?.activeCommonCriteriaVersion ? (
                                      <Text fontWeight={400} size="$sm">{`Current: v${item.activeVersionNbr}`}</Text>
                                    ) : (
                                      ''
                                    )}

                                    {item?.isActive === true && (
                                      <Badge outline variant="success">
                                        Active
                                      </Badge>
                                    )}

                                    {item?.isActive === false && (
                                      <Badge outline variant="error">
                                        Inactive
                                      </Badge>
                                    )}
                                  </Layout.Group>
                                </Heading>
                              </Accordion.Header>
                            </Accordion.Trigger>
                            <Accordion.Content>
                              <EditForm
                                currentEntity={item}
                                refetch={refetch}
                                setFocusedEntity={setFocusedEntity}
                                setShowAll={setShowAll}
                              />
                            </Accordion.Content>
                          </Accordion.Item>
                        );
                      })}
                    </Accordion>
                  )}
                </React.Fragment>
              )}
            </Grid.Col>
          </Grid>
        </Styles>
      </motion.div>
    </ErrorHandler>
  );
}
